import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import type { PreisToolBestOffer, PreisToolItem } from '@/api/preis-tool/preis-tool.types';
import { fmtEUR, type PriceField } from '../helpers';
import type { RowState } from '../row-states';
import { AnomalyDot } from './cells/anomaly-dot';
import { BedarfCell } from './cells/bedarf-cell';
import { EditablePriceCell, type CellNavDirection } from './cells/editable-price-cell';

export interface PriceDrafts {
  [itemId: string]: Partial<Record<PriceField, number | null>>;
}

export interface ActiveCell {
  rowIndex: number;
  colId: string;
}

export interface HitListTableMeta {
  drafts: PriceDrafts;
  onPriceCommit: (itemId: string, field: PriceField, value: number | null) => void;
  activeCell: ActiveCell | null;
  editingCell: ActiveCell | null;
  onCellNav: (dir: CellNavDirection) => void;
  initialKey: string | null;
  /** Legend states per item id (EOS-61); the dot shows for every "Deal". */
  rowStates: ReadonlyMap<string, readonly RowState[]>;
  /** "Niedrige Marge" limit for the margin pills. */
  lowMarginPct: number;
  /** Whether the current user may edit prices (`ITEMS_EDIT_PRICES`). */
  canEdit: boolean;
}

const col = createColumnHelper<PreisToolItem>();

const EDITABLE_COLS = ['standardPrice', 'itscopePrice', 'onlinerPrice'];

function priceColumn(field: PriceField, header: string): ColumnDef<PreisToolItem, any> {
  return col.accessor(field, {
    header,
    size: 120,
    minSize: 100,
    maxSize: 120,
    meta: { numeric: true },
    cell: ({ row, table }) => {
      const meta = table.options.meta as HitListTableMeta | undefined;
      const draft = meta?.drafts[row.original.id]?.[field];
      const isEditing = meta?.editingCell?.rowIndex === row.index && meta?.editingCell?.colId === field;
      return (
        <EditablePriceCell
          price={row.original[field]}
          mek={row.original.movingAveragePrice}
          draftPrice={draft}
          onCommit={(v) => meta?.onPriceCommit(row.original.id, field, v)}
          triggerEdit={isEditing}
          initialKey={isEditing ? meta?.initialKey ?? undefined : undefined}
          onNav={(dir) => meta?.onCellNav(dir)}
          canEdit={meta?.canEdit ?? false}
          openChange={row.original.openPriceChanges?.[field]}
          lowMarginPct={meta?.lowMarginPct}
        />
      );
    },
  });
}

export const hitListColumns: ColumnDef<PreisToolItem, any>[] = [
  col.display({
    id: 'anomaly',
    size: 22,
    enableSorting: false,
    cell: ({ row, table }) => {
      const meta = table.options.meta as HitListTableMeta | undefined;
      return (
        <AnomalyDot
          hasSupplierDeal={meta?.rowStates.get(row.original.id)?.includes('deal') ?? false}
        />
      );
    },
  }),
  col.accessor('id', {
    header: 'Art.-Nr.',
    size: 110,
    cell: (info) => <span className="id-cell">{info.getValue()}</span>,
  }),
  col.accessor('name', {
    header: 'Bezeichnung',
    size: 300,
    meta: { flex: true },
    cell: (info) => (
      <span className="name-cell" title={info.getValue()}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('movingAveragePrice', {
    header: 'MEK',
    size: 80,
    minSize: 70,
    meta: { numeric: true },
    cell: (info) => fmtEUR(info.getValue()),
  }),
  priceColumn('standardPrice', 'Standard'),
  priceColumn('itscopePrice', 'ITscope'),
  priceColumn('onlinerPrice', 'Onliner'),
  col.accessor('bestOffer', {
    id: 'bestOffer',
    header: 'Bester Lieferant',
    size: 170,
    minSize: 120,
    enableSorting: false,
    cell: (info) => <BestOfferCell offer={info.getValue()} />,
  }),
  col.accessor('quantityOnStock', {
    header: 'Lager',
    size: 55,
    minSize: 45,
    meta: { numeric: true },
  }),
  col.accessor('quantityOrderedByCustomers', {
    header: 'Bestätigt',
    size: 65,
    minSize: 55,
    meta: { numeric: true },
  }),
  col.accessor('quantityOrderedFromVendors', {
    header: 'Bestellt',
    size: 60,
    minSize: 50,
    meta: { numeric: true },
  }),
  col.display({
    id: 'bedarf',
    header: 'Bedarf',
    size: 60,
    minSize: 50,
    meta: { numeric: true },
    enableSorting: false,
    cell: ({ row }) => <BedarfCell item={row.original} />,
  }),
];

export { EDITABLE_COLS };

/** Supplier and net price of the item's best offer, as in its offer table. */
function BestOfferCell({ offer }: { offer: PreisToolBestOffer | null }) {
  if (!offer) return <span style={{ color: 'var(--ink-4)' }}>—</span>;
  const source = offer.source === 'fixed_price' ? 'Fixpreis' : offer.source === 'manual' ? 'manuell' : 'Feed';
  return (
    <span className="best-offer-cell" title={`${offer.supplierName} · € ${fmtEUR(offer.netPrice)} · ${source}`}>
      <span className="supplier">{offer.supplierName}</span>
      <span className="price">{fmtEUR(offer.netPrice)}</span>
    </span>
  );
}
